'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { Header } from '@udemy-v2/react-header';
import { LayoutBody, LayoutContainer, LayoutTitleArea, Sidebar, SidebarNormalFlow, SidebarPanel, SidebarPanelUnit, SidebarPopout, StickyFooter } from '@udemy-v2/react-sidebar-layout-components';
import { ColorScheme, tokens } from '@udemy-v2/styles';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { CourseBodyContent } from '@/features/course-landing-page/components/course-body-content/course-body-content.react-component';
import { CourseLede } from '@/features/course-landing-page/components/course-lede/course-lede.react-component';
import { Footer } from '@/components/footer';
import { SubscriptionMarketplacePurchaseContent } from '@/features/course-landing-page/components/marketplace-purchase-content/marketplace-purchase-content.react-component';
import { PremiumCourseNotice } from '@/features/course-landing-page/components/premium-course-notice/premium-course-notice.react-component';
import { StickyHeader } from '@/features/course-landing-page/components/sticky-header/sticky-header.react-component';
import { TeamsPurchaseContent } from '@/features/course-landing-page/components/teams-purchase-content/teams-purchase-content.react-component';
import { TeamsPurchaseSliderMenuContent } from '@/features/course-landing-page/components/teams-purchase-content/teams-purchase-slider-menu-content.react-component';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpPurchaseContext } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { useSubsDiff } from '@/features/course-landing-page/components/subs-diff/use-subs-diff.react-component';
import { useActivationOfferModal } from '@/features/course-landing-page/hooks/use-activation-offer-modal';
import { ActivationOfferModal } from '@udemy-v2/new-visitor-deal';
import styles from './course-landing-page.module.scss';
import { useEffect, memo, useMemo } from 'react';
import { publishEvent } from '@udemy-v2/event-tracking';
import { CLPViewEvent } from './events/events';
import { StickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/sticky-footer-content/sticky-footer-content.react-component';
import { PositionMarkerProvider } from '@/components/position-marker';
import { CourseSidebarContainer, SIDEBAR_PREVIEW_UI_REGION } from '@/features/course-landing-page/components/course-sidebar/course-sidebar-container.react-component';
import { CoursePreviewEntryPoint } from '@/features/course-landing-page/components/course-preview-entry-point/course-preview-entry-point.react-component';
import { useSmartBarRef } from '@udemy-v2/react-smart-bar';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';

/**
 * Indicates whether or not a given piece of content is rendered in the sidebar or the body
 */
export enum CourseContentPlacement {
  SIDEBAR = 'sidebar',
  BODY = 'body',
}

/**
 * Isolated, memoized component to render sidebar preview
 * This prevents re-renders when parent CourseLandingPage re-renders
 * Always rendered but hidden via CSS on mobile to prevent mount/unmount cycles
 *
 * Note: CoursePreviewEntryPoint will still re-render when scroll position changes
 * (via PositionMarkerProvider context), which is expected and necessary for the
 * fade-out animation. The memo here prevents re-renders from parent state changes.
 */
const SidebarPreview = memo(function SidebarPreview() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CoursePreviewEntryPoint uiRegion={SIDEBAR_PREVIEW_UI_REGION} hideWhenLedeOutOfView={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
});
export const CourseLandingPage = () => {
  const $ = _c(76);
  const course = useClpCourse();
  const hasOrganization = useUserHasOrganization();
  const {
    activeSidebarTab,
    renderTabContainer,
    unwrappedConsumerSubscriptionPlan,
    useCollapsiblePurchaseSection,
    subscriptionTiersEnabled
  } = useClpPurchaseContext();
  const isConsumerSubscriptionPlan = unwrappedConsumerSubscriptionPlan?.productType === SubscriptionPlanProductType.Consumersubscription || unwrappedConsumerSubscriptionPlan?.productType === SubscriptionPlanProductType.ConsumersubscriptionValuePlan;
  let t0;
  if ($[0] !== isConsumerSubscriptionPlan || $[1] !== subscriptionTiersEnabled) {
    t0 = !subscriptionTiersEnabled && isConsumerSubscriptionPlan ? <PremiumCourseNotice /> : null;
    $[0] = isConsumerSubscriptionPlan;
    $[1] = subscriptionTiersEnabled;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const premiumCourseNotice = t0;
  const isDesktop = useMatchMedia(`(min-width: ${tokens["viewport-lg-min"]})`);
  const {
    shouldAddSubsDiffSpacing,
    shouldShowSubsDiff,
    isSubsDiffLoading,
    SubsDiffComponent
  } = useSubsDiff();
  const {
    smartBarHeight,
    isSticky,
    isHidden
  } = useSmartBarRef();
  const {
    isOpen,
    showModal,
    hideModal,
    canShowModal
  } = useActivationOfferModal();
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    const params = new URLSearchParams({
      action: "nvd-login",
      mode: "marketplace-signup"
    });
    t1 = "/join/passwordless-auth/";
    t2 = params.toString();
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const signupUrl = `${t1}?${t2}`;
  const t3 = isSticky && !isHidden ? `${smartBarHeight}px` : 0;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      marginBlockStart: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const sidebarOffsetStyle = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CourseBodyContent />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const courseBodyContent = t5;
  let t6;
  let t7;
  if ($[8] !== course.frontendTrackingId || $[9] !== course.id || $[10] !== course.isDirectLanded) {
    t6 = () => {
      if (course.frontendTrackingId) {
        publishEvent(new CLPViewEvent({
          courseId: Number(course.id),
          courseTrackingId: course.frontendTrackingId ?? "",
          isDirectLanded: course.isDirectLanded ?? false
        }));
      }
    };
    t7 = [course.id, course.frontendTrackingId, course.isDirectLanded];
    $[8] = course.frontendTrackingId;
    $[9] = course.id;
    $[10] = course.isDirectLanded;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== canShowModal || $[14] !== showModal) {
    t8 = () => {
      if (canShowModal) {
        showModal();
      }
    };
    t9 = [canShowModal, showModal];
    $[13] = canShowModal;
    $[14] = showModal;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] !== premiumCourseNotice || $[18] !== renderTabContainer || $[19] !== useCollapsiblePurchaseSection) {
    t10 = placement => {
      const personalContent = <SubscriptionMarketplacePurchaseContent placement={placement} premiumCourseNotice={premiumCourseNotice} />;
      const teamsContent = <TeamsPurchaseContent />;
      return <CourseSidebarContainer placement={placement} personalContent={personalContent} teamsContent={teamsContent} renderTabContainer={renderTabContainer} useCollapsiblePurchaseSection={useCollapsiblePurchaseSection} />;
    };
    $[17] = premiumCourseNotice;
    $[18] = renderTabContainer;
    $[19] = useCollapsiblePurchaseSection;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const renderSidebarPanelContent = t10;
  let t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = classNames("ud-full-viewport-container", styles["layout-container"]);
    t12 = <Header />;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  const t13 = course.enrollments?.count;
  let t14;
  if ($[23] !== course.rating || $[24] !== course.title || $[25] !== t13) {
    t14 = <StickyHeader rating={course.rating} title={course.title} numEnrollments={t13} />;
    $[23] = course.rating;
    $[24] = course.title;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = isDesktop ? "dark" : "light";
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      backgroundColor: "transparent"
    };
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = shouldShowSubsDiff || isSubsDiffLoading;
  const t18 = shouldShowSubsDiff || isSubsDiffLoading;
  let t19;
  if ($[28] !== shouldAddSubsDiffSpacing) {
    t19 = classNames({
      [styles["course-lede-with-subs-diff"]]: shouldAddSubsDiffSpacing
    });
    $[28] = shouldAddSubsDiffSpacing;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = <CourseLede hideRating={t17} hideEnrollments={t18} className={t19} />;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== SubsDiffComponent || $[35] !== hasOrganization || $[36] !== isDesktop) {
    t21 = !isDesktop && !hasOrganization && <SubsDiffComponent />;
    $[34] = SubsDiffComponent;
    $[35] = hasOrganization;
    $[36] = isDesktop;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t15 || $[39] !== t20 || $[40] !== t21) {
    t22 = <LayoutTitleArea className={styles["title-area"]}><ColorScheme colorScheme={t15} style={t16}>{t20}{t21}</ColorScheme></LayoutTitleArea>;
    $[38] = t15;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <SidebarPreview />;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== sidebarOffsetStyle) {
    t24 = <div className={styles["sidebar-preview-wrapper"]} style={sidebarOffsetStyle}>{t23}</div>;
    $[43] = sidebarOffsetStyle;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== renderSidebarPanelContent) {
    t25 = <SidebarPopout><SidebarPanel className={styles["sidebar-container"]}><SidebarPanelUnit fullBleed={true}>{renderSidebarPanelContent(CourseContentPlacement.SIDEBAR)}</SidebarPanelUnit></SidebarPanel></SidebarPopout>;
    $[45] = renderSidebarPanelContent;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== renderSidebarPanelContent) {
    t26 = <SidebarNormalFlow><SidebarPanel className={styles["sidebar-container"]}><SidebarPanelUnit fullBleed={true}>{renderSidebarPanelContent(CourseContentPlacement.BODY)}</SidebarPanelUnit></SidebarPanel></SidebarNormalFlow>;
    $[47] = renderSidebarPanelContent;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t24 || $[50] !== t25 || $[51] !== t26) {
    t27 = <Sidebar className={styles.sidebar} isSticky={true}>{t24}{t25}{t26}</Sidebar>;
    $[49] = t24;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== activeSidebarTab) {
    t28 = <StickyFooter className={styles["sticky-footer"]}>{activeSidebarTab === "teams" ? <TeamsPurchaseSliderMenuContent /> : <StickyFooterContent />}</StickyFooter>;
    $[53] = activeSidebarTab;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  const t29 = shouldAddSubsDiffSpacing ? styles["body-container-with-subs-diff"] : styles["body-container"];
  let t30;
  if ($[55] !== SubsDiffComponent || $[56] !== hasOrganization || $[57] !== isDesktop) {
    t30 = isDesktop && !hasOrganization && <SubsDiffComponent />;
    $[55] = SubsDiffComponent;
    $[56] = hasOrganization;
    $[57] = isDesktop;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t29 || $[60] !== t30) {
    t31 = <LayoutBody className={t29}>{t30}{courseBodyContent}</LayoutBody>;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t22 || $[63] !== t27 || $[64] !== t28 || $[65] !== t31) {
    t32 = <LayoutContainer className={styles["layout-container"]}>{t22}{t27}{t28}{t31}</LayoutContainer>;
    $[62] = t22;
    $[63] = t27;
    $[64] = t28;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Footer />;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== canShowModal || $[69] !== hideModal || $[70] !== isOpen) {
    t34 = canShowModal && <ActivationOfferModal isOpen={isOpen} onClose={hideModal} signupUrl={signupUrl} method="GET" enableAutoOpen={true} />;
    $[68] = canShowModal;
    $[69] = hideModal;
    $[70] = isOpen;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t14 || $[73] !== t32 || $[74] !== t34) {
    t35 = <PositionMarkerProvider><div className={t11}>{t12}{t14}{t32}{t33}{t34}</div></PositionMarkerProvider>;
    $[72] = t14;
    $[73] = t32;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  return t35;
};